import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import NewWindow from "react-new-window";
import { useReactToPrint } from "react-to-print";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useBaselineItemsData } from "src/hooks/useBaselineItemsData";

// Layout/UI
import {
	Box,
	Button,
	Card,
	HStack,
	Icon,
	Input,
	RadioGroup,
	Stack,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import BaselineItemConfigs from "../Baseline/BaselineItemConfigs";
import ScenarioItems from "./ScenarioItems";
import ScenarioBudgetSummary from "./ScenarioBudgetSummary";
import { AppModal } from "src/components/UI/common/AppModal";
import FormMultiSelect from "src/components/UI/common/FormMultiSelect";
import { ModalShell } from "src/components/UI/common/ModalShell";
import styles from "./ScenarioItems.module.css";
import "./ScenarioSelections.css";
import {
	AddSquareRegular,
	SubtractSquareRegular,
	PanelSeparateWindowRegular,
	DismissSquareRegular,
} from "@fluentui/react-icons";

// Utilities
import { mColor, getContrastYIQ } from "src/utils/themeUtils";
import { getValuesByPropertyPath, filterObjectsByProperty, deepClone } from "src/utils/dataUtils";
import { isDeveloper } from "src/utils/sessionUtils";
import { debugLog, showTesting } from "src/utils/debugUtils";

const ScenarioSelections = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	useEffect(() => {
		debugLog(2, "[ScenarioSelections][FLOW] mounted", {});
	}, []);

	const pageMode = props.mode;
	const uiSize = props.size;

	const ref = useRef(null);
	const componentRef = useRef(null);

	// Get the selected scenario from Redux (single source of truth)
	const selectedScenario = useSelector((state) => state.scenarios.selectedScenario);

	// --- Local State ---
	// ================================================================
	// ================================================================
	// use Local State seems to be a better option for a complex component like this
	//
	// Local copy of the current baseline (driven from props)
	const [currScenario, setCurrScenario] = useState(deepClone(props.item));

	const [displayItemsBy, setDisplayItemsBy] = useState(111);
	const [column, setColumn] = useState("military_service_id");
	const [groups, setGroups] = useState(
		filterObjectsByProperty(currScenario.baseline_configs, "comp_type_id", parseInt(displayItemsBy)),
	);
	const [groupIdx, setGroupIdx] = useState(0);
	const [isExpanded, setIsExpanded] = useState(Array(groups.length).fill(false));
	const [modalOpen, setModalOpen] = useState(false);
	const initialGroup = displayItemsBy === 113 || displayItemsBy === 999 ? -1 : (groups?.[0]?.id ?? -1);
	const [groupBy, setGroupBy] = useState(initialGroup); // groupBy = -1 will display all items

	const [popupSummary, setPopupSummary] = useState(false);
	const [showSummary, setShowSummary] = useState(false);
	const [searchKeyword, setSearchKeyword] = useState("");
	const [itemsFoundByKeyword, setItemsFoundByKeyword] = useState([]);

	// Syncs local state with changes to props.item (from parent)
	useUpdateEffect(() => {
		setCurrScenario(deepClone(props.item));
		debugLog(3, "[ScenarioSelections][STATE] props.item changed", { propsItem: props.item });
	}, [props.item]);

	// ================================================================
	// ================================================================
	const readyActiveBaselineItems = useMemo(() => {
		const items = currScenario?.baseline_items;
		if (!Array.isArray(items)) return [];
		return items.filter((item) => Number(item?.status_id) === 1 && Number(item?.data_status_id) === 1);
	}, [currScenario?.baseline_items]);

	const visibleItems = useMemo(() => {
		const items = readyActiveBaselineItems;
		if (!Array.isArray(items)) return []; // <-- key safeguard

		if (parseInt(displayItemsBy) === 111) return items.filter((x) => x.military_service_id === groupBy);
		else if (parseInt(displayItemsBy) === 112) return items.filter((x) => x.domain_id === groupBy);
		else if (parseInt(displayItemsBy) === 113) {
			const categoryGroups = Array.isArray(groupBy) ? groupBy : [];
			return items.filter((x) => categoryGroups.indexOf(x.category_id) !== -1);
		} else if (parseInt(displayItemsBy) === 999) {
			if (searchKeyword.length === 0) return [];
			return itemsFoundByKeyword;
		}
	}, [readyActiveBaselineItems, displayItemsBy, groupBy, itemsFoundByKeyword, searchKeyword]);

	// Custom hooks
	const { lookups } = useLookupsData();

	const { currentItem, setCurrentItem, clearCurrentItem, fetchAndPrepareItem } = useBaselineItemsData(
		currScenario.baseline,
		lookups,
	);

	const displayItemsByChangeHandler = (event, selected) => {
		debugLog(3, "[ScenarioSelections][ACTION] displayItemsByChangeHandler", {
			selected: selected,
		});
		if (!selected) return true;
		const selectedId = parseInt(selected, 10);
		if (Number.isNaN(selectedId)) return true;

		setDisplayItemsBy(selectedId);
		// setIsExpanded(false);
		const thisGroup = filterObjectsByProperty(currScenario.baseline_configs, "comp_type_id", selectedId);

		// find next group with items
		let found = false;
		let column = "";
		switch (selectedId) {
			case 111:
				column = "military_service_id";
				break;
			case 112:
				column = "domain_id";
				break;
			case 113:
				column = "category_id";
				break;
			default:
				column = "name";
				break;
		}
		setColumn(column);

		const baselineItems = readyActiveBaselineItems;
		const mappedGroups = thisGroup.map((x) => {
			const tempArray = baselineItems.filter((y) => {
				return y[column] === x.id;
			});
			return {
				...x,
				name: x.comp_value,
				itemCount: tempArray.length,
			};
		});
		setGroups(mappedGroups);

		// Group by Category
		if (selectedId === 113) {
			setGroupBy([]);
		} else if (selectedId === 999) {
			setGroupBy(-1);
			setSearchKeyword("");
		} else {
			thisGroup.map((x) => {
				if (!found) {
					baselineItems.map((y) => {
						if (y[column] === x.id) {
							found = true;
							setGroupBy(x.id);
						}
						return false;
					});
				}
				return false;
			});
		}

		const element = document.getElementById("top");
		if (element) {
			// Will scroll smoothly to the top of the next section
			element.scrollIntoView({ behavior: "smooth" });
		}

		debugLog(3, "[ScenarioSelections][ACTION] displayItemsByChangeHandler", {
			selected: selectedId,
			column: column,
			groups: mappedGroups,
		});
	};

	const groupByChangeHandler = (event, selected) => {
		setGroupBy(selected);
		setGroupIdx(groups.findIndex((x) => x.id === selected));

		const lastChildElement = ref.current?.lastChildElement;
		lastChildElement?.scrollIntoView({ behavior: "smooth" });

		debugLog(3, "[ScenarioSelections][ACTION] groupByChangeHandler", {
			selected: selected,
			groupIdx: groups.findIndex((x) => x.id === selected),
		});
	};

	const groupByCategoryChangeHandler = (selected) => {
		setGroupBy([...getValuesByPropertyPath(selected, "id")]);
		debugLog(3, "[ScenarioSelections][ACTION] groupByCategoryChangeHandler", { selected: selected });
	};

	const modalCloseHandler = () => {
		setCurrentItem(null);
		setModalOpen(false);
		debugLog(3, "[ScenarioSelections][ACTION] modalCloseHandler", {});
	};

	const popupHandler = () => {
		setPopupSummary(!popupSummary);
		debugLog(3, "[ScenarioSelections][ACTION] popupHandler", { popupSummary: !popupSummary });
	};

	const showSummaryHandler = () => {
		setShowSummary(!showSummary);
		debugLog(3, "[ScenarioSelections][ACTION] showSummaryHandler", { showSummary: !showSummary });
	};

	const searchKeywordChangeHandler = (e) => {
		const searchKeyword = e.target.value;
		setSearchKeyword(searchKeyword);
		debugLog(7, "[ScenarioSelections][ACTION] searchKeywordChangeHandler", {
			searchKeyword: searchKeyword,
		});
	};

	const findByKeyword = () => {
		const keyword = String(searchKeyword ?? "");
		const safeKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const baselineItems = readyActiveBaselineItems;

		let tempArrayItem = [];
		try {
			const re = new RegExp(safeKeyword, "ig");
			tempArrayItem = baselineItems.filter((x) => x?.name?.match(re));
		} catch (error) {
			debugLog(2, "[ScenarioSelections][ERROR] findByKeyword.invalidRegex", {
				searchKeyword: keyword,
				error,
			});
		}
		setItemsFoundByKeyword(tempArrayItem);

		debugLog(3, "[ScenarioSelections][ACTION] findByKeyword", {
			searchKeyword: searchKeyword,
			found: tempArrayItem.length,
		});
	};

	const expandedSwitchHandler = (event, idx) => {
		setIsExpanded([
			...isExpanded.map((x, index) => {
				return index === idx ? !x : x;
			}),
		]);
		debugLog(3, "[ScenarioSelections][ACTION] expandedSwitchHandler", {
			idx: idx,
			isExpanded: !isExpanded[idx],
		});
	};

	const handleShowItem = (id) => {
		fetchAndPrepareItem(id);
		setModalOpen(true);
		debugLog(3, "[ScenarioSelections][ACTION] handleShowItem", { itemId: id });
	};

	const exportPdfSummaryHandler2 = useReactToPrint({
		content: () => componentRef.current,
	});

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioSelections][FLOW] render", {
		renderCount: renderCount.current,
		selectedScenario: selectedScenario,
		currScenario: currScenario,
		propsItem: props.item,
	});
	debugLog(9, "[ScenarioSelections][FLOW] renderState", {
		visibleItems: visibleItems,
		displayItemsBy: displayItemsBy,
		groups: groups,
		groupBy: groupBy,
		isExpanded: isExpanded,
	});
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	// ✅ TEMP test debug
	return (
		<div data-testid="ScenarioSelections">
			{modalOpen && currentItem && (
				<AppModal
					open={modalOpen}
					onClose={modalCloseHandler}
					style={{ background: "transparent", boxShadow: "none", maxWidth: "none", width: "auto" }}
				>
					{/* Keep the existing full-screen editing feel by reusing the shared modal
					    shell with the same 3% viewport inset this screen used before. */}
					<ModalShell inset="3%">
						<BaselineItemConfigs
							formFieldWidth={"100%"}
							baseline={currScenario?.baseline ?? currScenario}
							item={currentItem}
							mode="scenario"
							size={uiSize || "small"}
							onSetCurrentItem={() => void 0}
							onClearCurrentItem={clearCurrentItem}
							onSaveDataToDB={() => void 0}
							onClearComplete={() => void 0}
							onReloadCurrentItem={() => void 0}
							onReloadBaseline={() => void 0}
							onChooseItem={() => void 0}
							onSetDataChanged={() => void 0}
						/>
					</ModalShell>
				</AppModal>
			)}
			<Box minH="600px">
				<Box p={1} mb={10} overflow="auto">
					<Card.Root>
						<Card.Body p="1rem">
							<Stack gap="8">
								<Box flex="1 1 100%">
									<Box pt={2} pb={2}>
										<Text textStyle="md" mb="10px">
											Display Items by
										</Text>
										{/* Keep the single-select rows on RadioGroup primitives so their
										keyboard and screen-reader behavior stays aligned. */}
										<RadioGroup.Root
											value={String(displayItemsBy)}
											onValueChange={(details) =>
												displayItemsByChangeHandler(
													{ target: { value: String(details?.value) } },
													String(details?.value),
												)
											}
											aria-label="Display items by"
										>
											<HStack gap={3} alignItems="stretch">
												{[
													{ value: "111", testId: "by_service", label: "Service" },
													{ value: "112", testId: "by_domain", label: "Domain" },
													{
														value: "113",
														testId: "by_category",
														label: "Category",
													},
													{ value: "999", testId: "by_keyword", label: "Keyword" },
												].map((option) => {
													const isSelected =
														String(displayItemsBy) === option.value;
													return (
														<RadioGroup.Item
															key={option.value}
															value={option.value}
															data-testid={option.testId}
															p="10px"
															fontWeight="600"
															borderRadius="0"
															borderColor={isSelected ? "transparent" : "#fff"}
															borderWidth={isSelected ? "4px" : "1px"}
															bg={isSelected ? mColor.NavyBlue : "blue.100"}
															opacity={isSelected ? "1" : "0.45"}
															color={isSelected ? "white" : "blue.700"}
															boxShadow={isSelected ? "2px 3px 3px" : "0px"}
															cursor="pointer"
															_focus={{ boxShadow: "none" }}
															_focusVisible={{
																boxShadow:
																	"0 0 0 3px rgba(66, 153, 225, 0.55)",
															}}
															_hover={{
																bg: isSelected ? "blue.700" : "blue.200",
																borderColor: isSelected
																	? "transparent"
																	: "blue.400",
																opacity: isSelected ? 1 : 0.55,
															}}
														>
															<RadioGroup.ItemHiddenInput />
															<RadioGroup.ItemText>
																{option.label}{" "}
																{showTesting() && `-${option.value}`}
															</RadioGroup.ItemText>
														</RadioGroup.Item>
													);
												})}
											</HStack>
										</RadioGroup.Root>
									</Box>
									<Box pt={2} pb={2}>
										{showTesting() && (
											<>
												displayItemsBy: {displayItemsBy} / filter type: {column} /
												groupBy: {groupBy}
												<br />
											</>
										)}

										{(displayItemsBy === 111 || displayItemsBy === 112) && (
											<>
												<Text
													fontSize="0.875rem"
													fontWeight="500"
													mt="10px"
													mb="10px"
												>
													Group selected
												</Text>
												{/* Keep the color-driven group chooser on the same RadioGroup
												primitive so the tile visuals do not need custom keyboard logic. */}
												<RadioGroup.Root
													value={String(groupBy)}
													onValueChange={(details) =>
														groupByChangeHandler(
															{ target: { value: String(details?.value) } },
															Number(details?.value),
														)
													}
													aria-label="Group by"
												>
													<HStack gap="5px" alignItems="stretch" flexWrap="wrap">
														{groups &&
															groups.length > 0 &&
															groups.map((x) => {
																const bgColor =
																	x.color && x.color !== "null"
																		? x.color
																		: mColor.White;
																const fontColor =
																	x.color && x.color !== "null"
																		? getContrastYIQ(x.color)
																			? mColor.MidnightBlue
																			: mColor.White
																		: mColor.MidnightBlue;
																const baselineItems =
																	readyActiveBaselineItems;
																const tempArray = baselineItems.filter(
																	(y) => {
																		return y[column] === x.id;
																	},
																);

																// Only shows the group with items.
																if (tempArray.length === 0) return false;

																const isSelected = groupBy === x.id;

																return (
																	<RadioGroup.Item
																		key={x.id}
																		value={String(x.id)}
																		data-testid={`group-by-${x.comp_value
																			.replace(/\s+/g, "-")
																			.toLowerCase()}`}
																		borderRadius="0"
																		borderColor={
																			isSelected ? bgColor : "#fff"
																		}
																		borderWidth="4px"
																		bg={bgColor}
																		opacity={isSelected ? "1" : "0.45"}
																		color={fontColor}
																		boxShadow={
																			isSelected
																				? "0 0 0 2px rgba(66, 153, 225, 0.35), 0 4px 10px rgba(0,0,0,0.15)"
																				: "none"
																		}
																		cursor="pointer"
																		_focus={{ boxShadow: "none" }}
																		_focusVisible={{
																			boxShadow:
																				"0 0 0 3px rgba(66, 153, 225, 0.55)",
																		}}
																		_hover={{
																			bg: bgColor,
																			borderColor: bgColor,
																			opacity: isSelected ? 1 : 0.55,
																		}}
																	>
																		<RadioGroup.ItemHiddenInput />
																		<Box
																			width="80px"
																			height="80px"
																			backgroundColor={bgColor}
																			color={fontColor}
																		>
																			<Box p="4px">
																				<RadioGroup.ItemText>
																					{x.comp_value}{" "}
																					{showTesting() && -x.id}
																				</RadioGroup.ItemText>
																			</Box>
																		</Box>
																	</RadioGroup.Item>
																);
															})}
													</HStack>
												</RadioGroup.Root>
											</>
										)}
										{displayItemsBy === 113 && (
											<FormMultiSelect
												type={{ name: "Category selected" }} // label source for MultiSelect
												items={groups} // options: objects with { id, name/... }
												selected={groupBy}
												mode={pageMode} // enables disabled-state logic in MultiSelect
												size={uiSize || "small"}
												hideItemId={1}
												hideItemRule={!isDeveloper()}
												onUpdate={(selected /*, type*/) =>
													groupByCategoryChangeHandler(selected)
												} // adapter to your existing handler
												// {...(currentUser?.id == null ? { disabled: true } : {})}
											/>
										)}
										{displayItemsBy === 999 && (
											<Stack gap="16px">
												{/* Preserve the previous wrapper spacing for the keyword search controls. */}
												<Input
													data-testid="keyword-input"
													size="xs"
													width="300px"
													variant="subtle"
													placeholder="Search..."
													value={searchKeyword}
													onChange={(e) => searchKeywordChangeHandler(e, false)}
													aria-label="Search by keyword"
													className={styles.searchInput}
												/>
												<Button
													data-testid="keyword-search-btn"
													color="primary"
													width="100px"
													mb="10px"
													mt="5px"
													onClick={findByKeyword}
												>
													Search
												</Button>
											</Stack>
										)}
									</Box>
								</Box>

								{popupSummary && (
									<NewWindow
										onUnload={popupHandler}
										zIndex="1000"
										overflow="auto"
										features={{ width: "960px", height: "600px" }}
									>
										<ScenarioBudgetSummary
											item={currScenario}
											onLoad={popupHandler}
											onExportPdfSummary={() => void 0}
										/>
									</NewWindow>
								)}
								{!popupSummary && (
									<Box display="flex" alignItems="center" gap="8px">
										<MetreaTooltip
											content="Open in a new window"
										>
											<Icon
												as={PanelSeparateWindowRegular}
												boxSize="24px"
												onClick={popupHandler}
											/>
										</MetreaTooltip>

										{showSummary && (
											<MetreaTooltip
												content="Hide Summary Chart"
											>
												<Icon
													as={SubtractSquareRegular}
													boxSize="24px"
													onClick={showSummaryHandler}
												/>
											</MetreaTooltip>
										)}
										<Box>
											{!showSummary && (
												<Box display="flex" alignItems="center" gap="6px">
													<MetreaTooltip
														content="Show Summary Chart"
													>
														<Icon
															as={AddSquareRegular}
															boxSize="24px"
															onClick={showSummaryHandler}
														/>
													</MetreaTooltip>
													<Text textStyle="md">Show Summary Chart</Text>
												</Box>
											)}
										</Box>
									</Box>
								)}
								{popupSummary && (
									<Box display="flex" alignItems="center" gap="8px">
										<MetreaTooltip
											content="Close Summary Chart window"
										>
											<Icon
												as={DismissSquareRegular}
												boxSize="24px"
												onClick={popupHandler}
											/>
										</MetreaTooltip>
										Summary Chart is display in a separate window
									</Box>
								)}
								{showSummary && !popupSummary && (
									<Box>
										<div
											id="divToPrint"
											ref={componentRef}
											style={{
												display: "flex",
												justifyContent: "center",
											}}
										>
											<ScenarioBudgetSummary
												item={currScenario}
												onExportPdfSummary={exportPdfSummaryHandler2}
											/>
										</div>
									</Box>
								)}
							</Stack>
						</Card.Body>
					</Card.Root>
				</Box>

				<Box>
					{isExpanded[groupIdx] && (
						<>
							<MetreaTooltip content="Collapse all">
								<Icon
									as={SubtractSquareRegular}
									boxSize="24px"
									onClick={(event) => expandedSwitchHandler(event, groupIdx)}
								/>
							</MetreaTooltip>
						</>
					)}
					{!isExpanded[groupIdx] && (
						<>
							<MetreaTooltip content="Expand all">
								<Icon
									as={AddSquareRegular}
									data-testid="expand-all"
									boxSize="24px"
									onClick={(event) => expandedSwitchHandler(event, groupIdx)}
								/>
							</MetreaTooltip>
						</>
					)}
					<label className={styles.configLabel} style={{ fontSize: "1rem" }}>
						<strong>
							{isExpanded[groupIdx] ? "Collapse" : "Expand"} All
							<>
								&nbsp;({visibleItems.length} item
								{(visibleItems?.length ?? 0) > 1 ? "s" : ""})
							</>
						</strong>
					</label>
				</Box>
				<Box overflow="auto">
					{/* Preserve the previous compact item-list stack spacing on the direct v2 path. */}
					<Stack gap="8px" direction="column">
						<ScenarioItems
							key={`scenario-${currScenario.id || currScenario.uuid}`}
							revertKey={props.revertKey}
							item={currScenario}
							displayItemsBy={displayItemsBy}
							groupBy={groupBy}
							groups={groups}
							visibleItems={visibleItems}
							filter={{ keywords: searchKeyword }}
							mode={pageMode}
							isExpanded={isExpanded}
							onShowItem={handleShowItem}
							onChange={props.onChange}
							onError={props.onError}
							onSaveDataToDB={props.onSaveDataToDB}
						/>
						{/* Display extra space if items are grouped by Category */}
						{/* {Array.isArray(groupBy) && <Box minH="400px" backgroundColor="lightblue">&nbsp;</Box>} */}
						<div ref={ref} />
					</Stack>
				</Box>
			</Box>
		</div>
	);
};

export default ScenarioSelections;
